import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function FeatureAccordion({ title, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyRef = useRef(null);
    const [height, setHeight] = useState(defaultOpen ? 'auto' : '0px');

    useEffect(() => {
        if (!bodyRef.current) return;
        if (open) {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            const t = setTimeout(() => setHeight('auto'), 380);
            return () => clearTimeout(t);
        } else {
            const h = bodyRef.current.scrollHeight;
            setHeight(`${h}px`);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setHeight('0px'));
            });
        }
    }, [open]);

    return (
        <div style={{
            background: 'var(--surface)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 20px rgba(0,0,0,.04)',
            overflow: 'hidden'
        }}>
            <div
                style={{
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: open ? 'rgba(46, 204, 113, 0.05)' : 'transparent',
                    transition: 'background .3s ease'
                }}
                onClick={() => setOpen(v => !v)}
            >
                <h4 style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: 'var(--g)',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {title}
                </h4>
                <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform .4s var(--co-ease)',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            <div
                ref={bodyRef}
                style={{
                    overflow: 'hidden',
                    height: height,
                    transition: height === 'auto' ? 'none' : 'height .4s var(--co-ease), opacity .3s ease',
                    opacity: open ? 1 : 0
                }}
            >
                <div style={{ padding: '0 24px 20px 24px' }}>
                    <div style={{ paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ExtraDetailsPanel({
    extraDetails,
    lang,
    trimEnd,
}) {
    const { t: trans } = useTranslation();

    if (!extraDetails || extraDetails.length === 0) return null;

    return (
        <section className="sec rv vis" style={{ background: 'var(--bg)', padding: '50px 0' }}>
            <div className="ctn d3">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', padding: '0 8px' }}>
                    <span style={{ width: '4px', height: '18px', borderRadius: '2px', background: 'var(--co-green)' }}></span>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--t1)', margin: 0 }}>
                        {trans('new:key_features') || 'Key Features'}
                    </h3>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {extraDetails.map((details, index) => {
                        const note = details['note'];
                        if (
                            !note ||
                            note.split('\n').length === 0 ||
                            note.split('\n')[0] === 'null' ||
                            trimEnd(note).toString() === ''
                        ) return null;

                        const title = lang === 'ar' ? details?.sectionAr : details?.sectionEn;
                        const lines = note.split('\n').filter(line => trimEnd(line));

                        return (
                            <FeatureAccordion key={index} title={title} defaultOpen={index === 0}>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {lines.map((noteLine, idx) => trimEnd(noteLine) ? (
                                        <li key={idx} style={{
                                            color: 'var(--t1)',
                                            fontSize: '14.5px',
                                            lineHeight: '1.6',
                                            display: 'flex',
                                            gap: '12px'
                                        }}>
                                            <span style={{ color: 'var(--t2)', marginTop: '2px' }}>•</span>
                                            <span>{noteLine}</span>
                                        </li>
                                    ) : null)}
                                </ul>
                            </FeatureAccordion>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
